import { z } from 'zod';

/**
 * `packages/calendar` runtime types.
 *
 * Phase 1 scope (per Phase 1 ticket II.C.1, `06-timeline.md` §3, §5):
 *
 * - Calendars are JSON-serializable definitions loaded from packs.
 * - Plugin runtime is Phase 3. Leap rules are a CLOSED ENUM of named
 *   patterns; arbitrary JS leap predicates do not exist at this layer.
 * - `cycles` is the full `06-timeline.md` §4 N-cycle **bag** (#230, slice
 *   A1 of epic #228): a set of parallel named cycles, each `atomic`
 *   (period in days), `nested` (built from a child cycle, regular or
 *   irregular), or `open` (retrospectively-closed ASOIAF seasons), with
 *   optional `list` / `product` / `formula` labels (§4.3). `CyclesSchema`
 *   is **dual-accepting**: the canonical bag form *and* the narrowed
 *   legacy `{ year, week? }` object (gregorian, harptos, any persisted
 *   Phase-1 definition) both validate, with no data migration. The runtime
 *   engine that interprets the bag (`day -> position` / `day -> label`) is
 *   a SEPARATE slice (A2, #231); this slice is schema + types only.
 * - `anchor_canonical_day` ties the calendar's origin display date
 *   (first era, `year_one`, month 1, day 1) onto the shared, calendar-
 *   independent day axis (ADR-0006 §2). The canonical day count is a
 *   signed integer; negative values are pre-anchor (deep history).
 * - The exact shape of `leap_rule` for non-Gregorian non-Harptos calendars
 *   is intentionally minimal here; ticket #54 (Gregorian) and #55 (Harptos)
 *   may expand it.
 *
 * All public types are derived from Zod schemas via `z.infer`. This keeps
 * the wire format and the in-memory type in lock-step and gives us free
 * runtime validation when ingesting a pack.
 */

// ---------- localized strings ----------

/**
 * A locale-keyed dictionary of strings, per `03a-entities.md §5`. The
 * default-locale string sits under the `en` key (or whatever the pack
 * declares as its default); additional locales are added by pack locale
 * bundles. Phase 1 ships English-only catalogs; the structure must
 * support locale expansion without changing the wire format.
 *
 * Two surface forms are accepted:
 *
 * - A bare string (treated as the default-locale string for back-compat
 *   with test fixtures and short labels).
 * - A `{ locale: string }` map (the canonical form for shipped packs).
 *
 * Resolution to a runtime display string happens at the boundary — see
 * `resolveLocalized` in this package.
 */
export const LocalizedStringSchema = z.union([z.string().min(1), z.record(z.string(), z.string())]);
export type LocalizedString = z.infer<typeof LocalizedStringSchema>;

/**
 * Resolve a `LocalizedString` to a runtime display string. Resolution
 * chain: requested locale → exact match → declared default locale → first
 * declared key → empty string. Bare strings short-circuit to themselves.
 *
 * Locale-fallback resolution is intentionally simple at this layer; the
 * full ICU MessageFormat surface lives in the app-level i18n module and
 * is layered ABOVE this helper.
 */
export function resolveLocalized(
  value: LocalizedString | undefined,
  locale = 'en',
  defaultLocale = 'en',
): string {
  if (value === undefined) return '';
  if (typeof value === 'string') return value;
  if (Object.prototype.hasOwnProperty.call(value, locale)) {
    const v = value[locale];
    if (typeof v === 'string') return v;
  }
  if (Object.prototype.hasOwnProperty.call(value, defaultLocale)) {
    const v = value[defaultLocale];
    if (typeof v === 'string') return v;
  }
  const keys = Object.keys(value);
  if (keys.length > 0) {
    const first = keys[0]!;
    const v = value[first];
    if (typeof v === 'string') return v;
  }
  return '';
}

// ---------- leap rules ----------

/**
 * Closed enum of leap-year patterns. Each variant is a small, fully
 * declarative shape — no functions, no plugin escape hatch (that arrives
 * with the plugin runtime in Phase 3).
 *
 * - `none` — no leap rule; every year has the same month lengths.
 * - `every_n_years` — every Nth year is a leap year, offset by `phase`
 *   (a year `Y` is leap iff `((Y - phase) % every) === 0`). Suitable for
 *   simple "leap every 4 years from year 1" fantasy calendars.
 * - `julian` — every year divisible by 4 is a leap year. Kept as a named
 *   variant for clarity and to let format/parse hint at the historical
 *   context.
 * - `gregorian` — Julian, minus century years that are not divisible
 *   by 400. The actual Gregorian calendar ships in ticket #54.
 */
export const LeapRuleSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('none') }).strict(),
  z
    .object({
      kind: z.literal('every_n_years'),
      every: z.number().int().positive(),
      phase: z.number().int(),
    })
    .strict(),
  z.object({ kind: z.literal('julian') }).strict(),
  z.object({ kind: z.literal('gregorian') }).strict(),
]);
export type LeapRule = z.infer<typeof LeapRuleSchema>;

// ---------- month + weekday cycles ----------

/**
 * A single month's metadata. `length` is the number of days in the
 * month in a non-leap year. If `leap_length` is set, the month uses
 * that length in leap years instead (e.g. Gregorian February:
 * `{ length: 28, leap_length: 29 }`). Months without a `leap_length`
 * are unaffected by the calendar's leap rule.
 */
export const MonthDefinitionSchema = z
  .object({
    /** Stable identifier, used for display and i18n keys. */
    name: z.string().min(1),
    /**
     * Optional localized display label. When absent, formatters fall back
     * to a tidied form of `name` (per `03a-entities.md §5`). Phase 1
     * shipped calendars (`gregorian`, `harptos`) carry English labels;
     * pack locale bundles add translations.
     */
    label: LocalizedStringSchema.optional(),
    length: z.number().int().positive(),
    leap_length: z.number().int().positive().optional(),
    /**
     * Editorial flag — a "month" whose body is a single intercalary day
     * outside the normal child sequence (Harptos's festival days, Shire's
     * Midyear's Day). Pure metadata at this layer; the canonical day-count
     * engine treats every entry uniformly. UI and `recurs_as` rules
     * (Phase 2) read this flag to render festival days distinctly and to
     * decide whether parallel cycles advance through them.
     */
    intercalary: z.boolean().optional(),
  })
  .strict();
export type MonthDefinition = z.infer<typeof MonthDefinitionSchema>;

/** The months cycle. For Phase 1 this is always present. */
export const MonthsCycleSchema = z
  .object({
    months: z.array(MonthDefinitionSchema).min(1).readonly(),
  })
  .strict();
export type MonthsCycle = z.infer<typeof MonthsCycleSchema>;

/**
 * The weekdays cycle. Optional: a calendar can declare just months and
 * skip weekdays entirely. When present, the weekday at a given canonical
 * day is `((day - phase) mod labels.length)` with mathematical modulo so
 * negative day counts produce a well-defined weekday.
 *
 * `phase` is the index into `labels` of the weekday at canonical day 0.
 */
/**
 * A single weekday's metadata. Stable `name` is the locale-independent
 * identifier (used in display strings when no localized label resolves);
 * `label` is the optional localized display form. Two surface forms are
 * accepted in `WeekdaysCycle.labels` (see below) — this object form, or
 * a bare string (`name` only) for short test fixtures.
 */
export const WeekdayDefinitionSchema = z
  .object({
    name: z.string().min(1),
    label: LocalizedStringSchema.optional(),
  })
  .strict();
export type WeekdayDefinition = z.infer<typeof WeekdayDefinitionSchema>;

export const WeekdaysCycleSchema = z
  .object({
    /**
     * Either a list of objects with `{ name, label? }` (the canonical
     * shape for shipped calendars) or a list of bare strings (a shorthand
     * for `{ name: <s> }`, kept for test fixtures and concise calendars).
     */
    labels: z
      .array(z.union([z.string().min(1), WeekdayDefinitionSchema]))
      .min(1)
      .readonly(),
    phase: z.number().int().nonnegative(),
  })
  .strict();
export type WeekdaysCycle = z.infer<typeof WeekdaysCycleSchema>;

/**
 * Normalize a `WeekdaysCycle.labels` entry to its `{ name, label? }`
 * object form. Pure projection — callers that just want the stable
 * identifier (the same value the engine uses to project a weekday) can
 * read `.name`.
 */
export function normalizeWeekday(entry: string | WeekdayDefinition): WeekdayDefinition {
  return typeof entry === 'string' ? { name: entry } : entry;
}

/**
 * The narrowed legacy cycles object: a months cycle (`year`) and an
 * optional weekdays cycle (`week`). Phase 1 shipped exactly this shape
 * (gregorian, harptos). #230 lifts the narrowing — the canonical form is
 * now the N-cycle *bag* (`CycleBagSchema` below) — but legacy `{ year,
 * week }` definitions (bundled or persisted) must keep validating with no
 * data migration, so this object form is retained as one arm of the
 * dual-accepting `CyclesSchema`. The conversion engine continues to read
 * `calendar.cycles.year.months` / `.week` off this arm directly; the
 * `normalizeCycles` helper folds it into the bag form on demand.
 */
export const LegacyCyclesSchema = z
  .object({
    year: MonthsCycleSchema,
    week: WeekdaysCycleSchema.optional(),
  })
  .strict();
export type LegacyCycles = z.infer<typeof LegacyCyclesSchema>;

// ---------- N-cycle "bag" (06-timeline.md §4) ----------

/**
 * #230 / `06-timeline.md` §4 — the N-cycle bag. A calendar declares a
 * **set of parallel named cycles**; each is, in essence, a pure function
 * `day -> position` (and optionally `day -> label`). Some cycles nest
 * inside others (a Month built from Days); some run alongside (a Week of
 * 7 days ignoring Month boundaries). The set is a *bag* of named cycles —
 * not a stack, not a strict hierarchy.
 *
 * This slice (A1, #230) is **schema + types only**. The runtime engine
 * that interprets the bag (`day -> position` / `day -> label`) is A2
 * (#231); nothing here computes a position or a label.
 */

/**
 * One instance of a nested cycle (§4): the `length` is the count of the
 * smaller (child) cycle's units this instance contains — e.g. a month is
 * `{ name: "Sowing", length: 30 }` over a day cycle. `intercalary` marks a
 * festival / leap day that sits outside the parent's normal child
 * sequence (Shire's Midyear's Day). Pure metadata at this layer.
 */
export const CycleInstanceSchema = z
  .object({
    name: z.string().min(1).optional(),
    length: z.number().int().positive(),
    intercalary: z.boolean().optional(),
  })
  .strict();
export type CycleInstance = z.infer<typeof CycleInstanceSchema>;

/**
 * One instance of an **open** cycle (§4.1): a temporal segment whose end
 * is determined retrospectively (ASOIAF seasons, Wheel of Time Ages).
 * `start_day` is a canonical day; `end_day` unset means "still open"
 * ("winter is coming"). A GM `close_at(day)` action fills `end_day` in a
 * later slice (A3, #232).
 */
export const OpenCycleInstanceSchema = z
  .object({
    name: z.string().min(1).optional(),
    start_day: z.number().int(),
    end_day: z.number().int().optional(),
  })
  .strict();
export type OpenCycleInstance = z.infer<typeof OpenCycleInstanceSchema>;

/**
 * How a nested cycle's instances are sized (§4.1):
 *
 * - `regular` — every instance contains the same number of children
 *   (Bahá'í 19-day months; a year of 12 months).
 * - `irregular` — an explicit per-instance list (Gregorian months
 *   `[31,28,31,30,…]`).
 */
export const NestedPatternSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('regular'),
      children_per_instance: z.number().int().positive(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('irregular'),
      per_instance: z.array(CycleInstanceSchema).min(1).readonly(),
    })
    .strict(),
]);
export type NestedPattern = z.infer<typeof NestedPatternSchema>;

/**
 * A cycle's structure — one of the three flavors from §4.1:
 *
 * - `atomic` — a fixed period in days, no internal structure (7-day week,
 *   10-day tenday, 60-day stem-branch, 260-day Tzolkin).
 * - `nested` — composed of instances of a smaller named cycle
 *   (`based_on`), sized by a `NestedPattern` (month built from days, year
 *   built from months).
 * - `open` — explicit instances with `start_day` and an optionally-unset
 *   `end_day` (§4.1; closed retrospectively).
 */
export const CycleStructureSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('atomic'),
      period_days: z.number().int().positive(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('nested'),
      based_on: z.string().min(1),
      pattern: NestedPatternSchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal('open'),
      instances: z.array(OpenCycleInstanceSchema).min(1).readonly(),
    })
    .strict(),
]);
export type CycleStructure = z.infer<typeof CycleStructureSchema>;

/**
 * A cycle's optional named-position output (§4.3):
 *
 * - `list` — `labels[position % period]` (weekday names, month names).
 * - `product` — label = join of factor cycles' labels; period = LCM of
 *   the factors' periods (Chinese sexagenary 10×12, Tibetan Rabjung).
 * - `formula` — a declarative template string the formatter expands with
 *   placeholders bound to other cycles' positions.
 *
 * Declared here so a `CycleDefinition` can carry it; the engine that
 * *evaluates* a `LabelSpec` lands with A2/A4 (#231/#233).
 */
export const LabelSpecSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('list'),
      labels: z.array(z.string().min(1)).min(1).readonly(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('product'),
      factors: z
        .array(z.object({ cycle: z.string().min(1) }).strict())
        .min(1)
        .readonly(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('formula'),
      format: z.string().min(1),
    })
    .strict(),
]);
export type LabelSpec = z.infer<typeof LabelSpecSchema>;

/**
 * A single named cycle in the bag (§4):
 *
 * - `name` — GM-chosen identifier ("tenday", "month", "stem-branch-year").
 * - `structure` — atomic / nested / open (`CycleStructureSchema`).
 * - `labels` — optional named-position output (`LabelSpecSchema`, §4.3).
 * - `intercalary.advances_cycle` — whether an intercalary day advances
 *   *this* cycle's phase. Per-cycle because parallel cycles can disagree
 *   (Shire week does NOT advance over Midyear's Day; Harptos tenday DOES —
 *   §4.2). Pure metadata here; the A2/A3 engine honors it.
 */
export const CycleDefinitionSchema = z
  .object({
    name: z.string().min(1),
    structure: CycleStructureSchema,
    labels: LabelSpecSchema.optional(),
    intercalary: z.object({ advances_cycle: z.boolean() }).strict().optional(),
  })
  .strict();
export type CycleDefinition = z.infer<typeof CycleDefinitionSchema>;

/** The N-cycle bag: a non-empty set of parallel named cycles (§4). */
export const CycleBagSchema = z.array(CycleDefinitionSchema).min(1).readonly();
export type CycleBag = z.infer<typeof CycleBagSchema>;

/**
 * `CyclesSchema` is **dual-accepting** (#230 back-compat, no data
 * migration): it validates EITHER
 *
 * - the canonical N-cycle bag (`CycleDefinition[]`), or
 * - the narrowed legacy `{ year, week? }` object (gregorian, harptos, and
 *   any persisted Phase-1 definition).
 *
 * The order matters: the bag is an array and the legacy form is an object,
 * so the two arms are structurally disjoint and the union never
 * mis-dispatches. The conversion engine reads the legacy arm directly;
 * `normalizeCycles` folds either arm into the bag form on demand.
 */
export const CyclesSchema = z.union([CycleBagSchema, LegacyCyclesSchema]);
export type Cycles = z.infer<typeof CyclesSchema>;

/** A `Cycles` value is the bag form iff it is an array. */
export function isCycleBag(cycles: Cycles): cycles is CycleBag {
  return Array.isArray(cycles);
}

/**
 * Fold any `Cycles` value into the canonical N-cycle bag form (§4).
 *
 * - A bag is returned unchanged (idempotent).
 * - The legacy `{ year, week? }` object is projected:
 *   - `year` becomes a `nested` cycle (`based_on: "month"`, irregular over
 *     the declared month lengths) carrying the month names as a `list`
 *     label;
 *   - `month` becomes a `nested` cycle (`based_on: "day"`, irregular over
 *     the per-month lengths) — the named-instance sequence the runtime
 *     engine (A2) walks for `day -> month/day-of-month`;
 *   - `week`, when present, becomes an `atomic` cycle whose period is the
 *     weekday count, carrying the weekday names as a `list` label and an
 *     **advancing** intercalary policy: a legacy weekday cycle counts every
 *     canonical day (festivals included), which is (a) the canonical §4.2
 *     behavior — the Harptos *tenday* (a 10-entry legacy `week`) ADVANCES
 *     over festival days — and (b) byte-identical to the pre-A3 weekday
 *     projection, which counted `(day - phase) mod period` with no
 *     intercalary skipping. The NON-advancing case (Shire 7-day week) is a
 *     hand-authored bag cycle that declares `advances_cycle: false`
 *     explicitly; it is not reachable through this legacy bridge (#232 §4.2).
 *
 * This is the migration-free bridge: no stored definition changes shape;
 * callers that want a uniform bag view call this at the boundary.
 */
export function normalizeCycles(cycles: Cycles): CycleBag {
  if (isCycleBag(cycles)) return cycles;

  const monthInstances: CycleInstance[] = cycles.year.months.map((m) => {
    const instance: CycleInstance = { name: m.name, length: m.length };
    if (m.intercalary !== undefined) instance.intercalary = m.intercalary;
    return instance;
  });
  const monthLabels: LabelSpec = {
    kind: 'list',
    labels: cycles.year.months.map((m) => m.name),
  };

  const bag: CycleDefinition[] = [
    {
      name: 'month',
      structure: { kind: 'nested', based_on: 'day', pattern: { kind: 'irregular', per_instance: monthInstances } },
      labels: monthLabels,
    },
    {
      name: 'year',
      structure: {
        kind: 'nested',
        based_on: 'month',
        pattern: { kind: 'regular', children_per_instance: monthInstances.length },
      },
    },
  ];

  const week = cycles.week;
  if (week !== undefined) {
    bag.push({
      name: 'week',
      structure: { kind: 'atomic', period_days: week.labels.length },
      labels: { kind: 'list', labels: week.labels.map((w) => normalizeWeekday(w).name) },
      // Advancing: a legacy weekday/tenday counts every day, festivals
      // included (canonical §4.2 Harptos tenday; byte-identical to pre-A3).
      intercalary: { advances_cycle: true },
    });
  }

  return bag;
}

// ---------- eras ----------

/**
 * An era is a non-repeating named segment of the timeline with its own
 * year numbering (per `06-timeline.md` §5).
 *
 * - `start_day` is the canonical day on which the era begins. The first
 *   era in the array may carry `start_day: null` to mean "from the
 *   beginning of time" (allows dates before any explicit boundary).
 * - `year_one` is the display year number of the era's first year.
 *   Forward-numbered eras have a positive `year_one` (e.g. "CE"
 *   starts at year 1); backward-numbered eras (`BCE`) are modeled as
 *   a separate era with `direction: 'backward'`.
 * - `day_offset` (issue #240, §5) splices the DISPLAYED day count at the
 *   era boundary while the underlying canonical day count stays
 *   monotonic. The historical Julian → Gregorian reform "skipped" 10
 *   calendar days: the canonical axis keeps ticking by 1, but inside the
 *   reformed era the decomposition into year/month/day is shifted forward
 *   by `day_offset` days. Defaults to 0 (no splice).
 * - `numbering` (issue #241, §5) optionally overrides the era's
 *   year-numbering scheme. When absent, the era numbers years ordinally
 *   per its `direction` (the back-compat default). A `named_years`
 *   numbering replaces the ordinal display year with a name drawn from a
 *   repeating list (`names`, period `cycle`) — the §3.1 "renamed fantasy
 *   / Year of the King" path. The underlying year axis is still ordinal
 *   forward (the conversion engine is unchanged); only the *display label*
 *   for the year changes. `direction` stays declared (a `named_years` era
 *   is forward-axised) so existing eras validate without a key change.
 */
export const EraDirectionSchema = z.enum(['forward', 'backward']);
export type EraDirection = z.infer<typeof EraDirectionSchema>;

/**
 * Era year-numbering override (§5). The `forward` / `backward` variants
 * are explicit forms of the ordinal numbering already driven by
 * `Era.direction`; `named_years` is the cycle-of-named-years scheme
 * ("Year of the Stag, …") that draws the display year from a repeating
 * `names` list with period `cycle`.
 */
export const EraNumberingSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('forward') }).strict(),
  z.object({ kind: z.literal('backward') }).strict(),
  z
    .object({
      kind: z.literal('named_years'),
      /** The repeating list of year-names; the first entry is `year_one`. */
      names: z.array(z.string().min(1)).min(1).readonly(),
      /** Period of the name cycle (how many years before `names` repeats). */
      cycle: z.number().int().positive(),
    })
    .strict(),
]);
export type EraNumbering = z.infer<typeof EraNumberingSchema>;

export const EraSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    /** Optional localized display label; falls back to `name` when absent. */
    label: LocalizedStringSchema.optional(),
    abbreviation: z.string().min(1).optional(),
    /** Canonical day on which this era begins; `null` only for a leading era. */
    start_day: z.number().int().nullable(),
    year_one: z.number().int(),
    direction: EraDirectionSchema,
    /**
     * Display-only day splice for historical reforms (§5, #240). The
     * canonical axis is untouched; only the year/month/day decomposition
     * inside this era is shifted by `day_offset` days. Optional; an absent
     * value is treated as 0 by the conversion engine (`era.day_offset ?? 0`)
     * so eras authored before #240 validate and convert byte-identically.
     */
    day_offset: z.number().int().optional(),
    /**
     * Optional year-numbering override (§5, #241). When absent the era
     * numbers years ordinally per `direction`. A `named_years` override
     * renders cycling year-names instead of ordinals (display-only).
     */
    numbering: EraNumberingSchema.optional(),
  })
  .strict();
export type Era = z.infer<typeof EraSchema>;

// ---------- display date ----------

/**
 * A display date in some calendar's coordinates. `month` and `day` are
 * 1-indexed. `era` is the era id; if omitted on input, the calendar
 * uses the era covering the resulting canonical day (typically the
 * default forward era).
 *
 * For Phase 1 the engine only accepts FULL dates (era + year + month +
 * day). Partial dates (just year, or year + month) are deferred — see
 * the open question in the ticket; this matches `06-timeline.md` §2's
 * "precision marker is editorial; the stored value is always a day"
 * stance — partial dates are an authoring affordance, not a runtime
 * representation.
 */
export const DisplayDateSchema = z
  .object({
    era: z.string().min(1),
    year: z.number().int(),
    month: z.number().int().positive(),
    day: z.number().int().positive(),
  })
  .strict();
export type DisplayDate = z.infer<typeof DisplayDateSchema>;

/**
 * Result of `toDisplayDate`: a DisplayDate enriched with the projected
 * weekday (if the calendar declares one). The weekday is read-only —
 * never an input to `toCanonicalDay`.
 */
export interface ExpandedDisplayDate extends DisplayDate {
  weekday?: string;
}

// ---------- formatter override (06-timeline.md §3, #243) ----------

/**
 * `formatter_override` (§3, slice C2 / #243) — a per-calendar display
 * escape hatch usable by **structured** calendars. When present it renders
 * the canonical `WorldTime` / day **directly**, bypassing cycle
 * decomposition, for calendars where decomposing into year/month/day is
 * not meaningful (Star Trek Stardates, Warhammer 40K Imperial Dating).
 *
 * v1 supports a **closed enum** of named built-in formatters — NO arbitrary
 * JS / plugin modules (consistent with the leap-rule closed-enum stance;
 * arbitrary formatter modules wait for the Phase-3 plugin runtime, slice
 * C1 / #242). An unknown ref **degrades** to the structured formatter with
 * a notice (see `formatWithFormatterOverride` in `format.ts`).
 *
 * The override is **display-only**: a calendar carrying one still
 * parses/compares through its cycles (`toCanonicalDay` / `toDisplayDate`
 * are untouched). The override only changes how a canonical day is
 * *rendered* for default display.
 */
export const BUILTIN_FORMATTER_NAMES = ['stardate', 'imperial_dating'] as const;
export type BuiltinFormatterName = (typeof BUILTIN_FORMATTER_NAMES)[number];

/**
 * A reference to a display formatter (§3, #243). v1 has a single variant:
 * `builtin`, naming one of the closed-enum built-in formatters. The
 * discriminated-union shape is intentional — a future `plugin` variant
 * (Phase 3, plugin runtime) slots in alongside without a wire break, and
 * the schema validates the `name` LOOSELY (any non-empty string) so an
 * unknown / forward-version formatter name still round-trips through the
 * op log and degrades gracefully at render time rather than failing
 * validation. The render-time dispatch is the closed enum; the schema is
 * the open envelope.
 */
export const FormatterRefSchema = z
  .object({
    kind: z.literal('builtin'),
    name: z.string().min(1),
  })
  .strict();
export type FormatterRef = z.infer<typeof FormatterRefSchema>;

// ---------- calendar mode + plugin contract (06-timeline.md §3, #242) ----------

/**
 * A calendar's authoring **mode** (§3, slice C1 / #242):
 *
 * - `structured` — the common path. The engine parses, formats, and reasons
 *   over the calendar from declarative cycle / era / leap data. Every Phase-1
 *   calendar (gregorian, harptos, every homebrew definition) is structured;
 *   a definition with no `mode` field **defaults to `structured`** so all
 *   existing calendars validate and behave byte-identically.
 * - `plugin` — for calendars the structured schema cannot express
 *   (variable-length canonical hours, era-restructured month sets,
 *   observationally-determined lunisolar leap months, relativistic
 *   multi-stream time). The pack ships a plugin **module** implementing the
 *   {@link CalendarPlugin} contract; the engine never decomposes such a
 *   calendar into cycles.
 *
 * **No plugin module executes in this slice.** v1 declares the contract and
 * loads a plugin definition (its pack's entities/traits/types stay
 * available), but calendar-dependent surfaces render a "calendar not fully
 * supported" notice instead of running plugin code — the Phase-3 plugin
 * runtime owns execution (tracked separately).
 */
export const CalendarModeSchema = z.enum(['structured', 'plugin']);
export type CalendarMode = z.infer<typeof CalendarModeSchema>;

/**
 * A capability a plugin calendar declares it provides (§3). The pack author
 * lists which features its module backs so a future runtime can light each
 * surface up incrementally (and so the degrade notice can name precisely
 * what is missing). v1 stores these opaquely — nothing reads them to gate
 * behavior yet; they are forward-declaration metadata.
 *
 * Mirrors the contract entry points: `format` / `parse` / `picker_descriptor`
 * are the surfaces a plugin must back to render dates and offer a date
 * picker; `compare` is the optional total-order hook (the engine falls back
 * to canonical-day order when absent).
 */
export const PluginCapabilitySchema = z.enum(['format', 'parse', 'picker_descriptor', 'compare']);
export type PluginCapability = z.infer<typeof PluginCapabilitySchema>;

/**
 * The `plugin { module_id, capabilities }` shape (§3, #242). Carried on a
 * `mode: "plugin"` calendar definition.
 *
 * - `module_id` — an opaque handle the Phase-3 plugin runtime resolves to a
 *   loaded module. NOT a URL and NOT executed here; it round-trips through
 *   the op log opaquely (the `@dl/shared` envelope is `.passthrough()`).
 * - `capabilities` — the {@link PluginCapability} list the module declares.
 *   At least one capability is required (a plugin that provides nothing is a
 *   malformed declaration).
 */
export const PluginSpecSchema = z
  .object({
    module_id: z.string().min(1),
    capabilities: z.array(PluginCapabilitySchema).min(1).readonly(),
  })
  .strict();
export type PluginSpec = z.infer<typeof PluginSpecSchema>;

/**
 * A descriptor a plugin returns so a host can render a date picker WITHOUT
 * knowing the calendar's internal structure (§3). A typed shape only — no
 * code in this slice constructs or consumes one; it documents the contract
 * the Phase-3 runtime will fulfil.
 *
 * Each field is a labelled, ordered list of selectable values the picker
 * renders as a column (era → year-ish → month-ish → day-ish), echoing the
 * structured date picker without committing the host to cycle semantics.
 */
export interface PluginPickerField {
  /** Stable identifier for this column (e.g. `"era"`, `"month"`). */
  readonly key: string;
  /** Localized column heading. */
  readonly label: LocalizedString;
  /** The selectable options, in display order. */
  readonly options: readonly { readonly value: string; readonly label: LocalizedString }[];
}

/**
 * A plugin's date-picker descriptor (§3): the ordered set of fields the host
 * renders, plus the canonical day the current selection maps to. Typed only;
 * the Phase-3 runtime produces it.
 */
export interface PluginPickerDescriptor {
  readonly fields: readonly PluginPickerField[];
  /** The canonical day the descriptor's default / current selection resolves to. */
  readonly canonical_day: number;
}

/**
 * The result of a plugin `compare` (§3) — the §2 comparison vocabulary. The
 * engine falls back to canonical-day order when a plugin omits `compare`.
 */
export type PluginCompareResult = 'before' | 'after' | 'equal' | 'overlaps' | 'uncertain';

/**
 * The **typed plugin contract** a `mode: "plugin"` calendar's module
 * implements (§3, #242). This is a documentation / type-only interface: NO
 * code in this slice (or anywhere in the shipped engine) loads a module,
 * constructs a `CalendarPlugin`, or invokes one of these methods. The
 * Phase-3 plugin runtime owns instantiation and sandboxed execution; this
 * interface fixes the surface that runtime must satisfy so pack authors can
 * declare a conforming plugin today.
 *
 * The four entry points mirror the structured engine's chokepoints:
 *
 * - `format(day, options?)` — render a canonical day to a display string
 *   (the plugin's analogue of {@link DisplayDate} formatting).
 * - `parse(input)` — resolve a display string back to a canonical day, or
 *   `null` when it does not parse (the analogue of `toCanonicalDay`).
 * - `picker_descriptor(day)` — produce a {@link PluginPickerDescriptor} so a
 *   host can render a structure-agnostic date picker.
 * - `compare(a, b)` — OPTIONAL total-order hook over canonical days; when
 *   absent the engine compares the underlying canonical day integers.
 */
export interface CalendarPlugin {
  /** Render a canonical day to a display string. */
  format(day: number, options?: { readonly locale?: string }): string;
  /** Resolve a display string to a canonical day, or `null` when unparseable. */
  parse(input: string): number | null;
  /** Produce a structure-agnostic date-picker descriptor for a canonical day. */
  picker_descriptor(day: number): PluginPickerDescriptor;
  /** OPTIONAL total order over canonical days (§2 vocabulary). */
  compare?(a: number, b: number): PluginCompareResult;
}

// ---------- anchor ----------

/**
 * The canonical calendar's named origin event (ADR-0006 §3). Makes the
 * temporal origin itself first-class: rather than an anonymous abstract
 * zero, canonical day 0 *is* a piece of the world ("The Founding", "The
 * Cataclysm"). Definitional, not computed.
 *
 * - `name` — a mandatory `LocalizedString` for the origin event.
 * - `event_ref` — an optional opaque entity id linking the origin to the
 *   event entity. It is a label / link, NOT a date that gets calculated
 *   from facts (which resolves the apparent circularity: a fact carries
 *   validity in canonical days, yet the anchor defines day 0).
 */
export const AnchorEventSchema = z
  .object({
    name: LocalizedStringSchema,
    event_ref: z.string().min(1).optional(),
  })
  .strict();
export type AnchorEvent = z.infer<typeof AnchorEventSchema>;

// ---------- calendar ----------

/**
 * A calendar definition. JSON-serializable; the runtime never embeds
 * functions in this shape (the `leap_rule` enum is the only branching
 * point and is fully declarative).
 *
 * `anchor_canonical_day` declares where this calendar's origin display
 * date (first era, `year_one`, month 1, day 1) sits on the shared
 * canonical day axis (ADR-0006 §2). A calendar is a pure bijection onto
 * that axis; `anchor_canonical_day: 0` puts its origin at canonical day 0.
 *
 * `anchor` (optional) names the canonical calendar's origin as an
 * in-world event (ADR-0006 §3) — "The Founding", "The Cataclysm". It is
 * definitional, not computed: `event_ref` links to the event entity, it
 * is never a date calculated from facts. Secondary / display calendars
 * typically omit it and simply project onto the same axis via their own
 * `anchor_canonical_day` offset.
 */
export const CalendarSchema = z
  .object({
    id: z.string().min(1),
    /**
     * Display label, either a bare string (legacy / test fixtures) or a
     * `LocalizedString` map (canonical shape for shipped calendars).
     */
    label: LocalizedStringSchema,
    /**
     * The runtime calendar the conversion engine consumes carries the
     * **legacy** `{ year, week? }` cycles arm. #230 lifted the *definition*
     * schema to the dual-accepting bag; A2 (#231) makes `asCalendar()`
     * *interpret* a bag-form definition by projecting it INTO this legacy
     * arm (`legacyCyclesFromBag` in `calendar-definition.ts`) — so a
     * bag-form calendar renders, while the runtime `Calendar` type stays
     * the narrow `{ year, week }` shape every existing consumer
     * (`@dl/ui`, `@dl/client` date pickers / settings) reads directly. The
     * general multi-cycle runtime (`cyclePosition` / `cycleLabel`) operates
     * on the bag via `normalizeCycles`, so atomic/nested resolution is
     * available regardless of which arm the runtime carries.
     */
    cycles: LegacyCyclesSchema,
    eras: z.array(EraSchema).min(1).readonly(),
    leap_rule: LeapRuleSchema,
    /**
     * Canonical day on which this calendar's origin display date (first
     * era, `year_one`, month 1, day 1) sits — the calendar's anchor onto
     * the shared day axis (ADR-0006 §2). A signed integer; defaults are
     * `0` for a calendar whose origin coincides with canonical day 0.
     */
    anchor_canonical_day: z.number().int(),
    /**
     * Optional named in-world origin event for the canonical calendar
     * (ADR-0006 §3). Definitional, not computed — `event_ref` is a link
     * to the origin event entity, never a date derived from facts.
     */
    anchor: AnchorEventSchema.optional(),
    /**
     * Optional display escape hatch (§3, #243). When present, default
     * display renders the canonical day directly through the named
     * built-in formatter, bypassing cycle decomposition; parse/compare
     * still go through the cycles (display-only). Carried through from a
     * `CalendarDefinition` by `asCalendar`'s spread.
     */
    formatter_override: FormatterRefSchema.optional(),
  })
  .strict();
export type Calendar = z.infer<typeof CalendarSchema>;

// `CanonicalDay` lives in `instant.ts` (the day-axis chokepoint, ADR-0006
// §1). Re-exported here for back-compat with existing imports from
// `./types.js`; all construction / arithmetic / (de)serialization must go
// through `instant.ts`.
export type { CanonicalDay } from './instant.js';
